import { Card, Descriptions } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactJson, { type ReactJsonViewProps } from 'react-json-view';
import { useNavigate } from 'react-router-dom';
import mergeTranslations from '../../../../server/utils/mergeTranslations';
import { type LanguagePackSpecsFragment } from '../../../api/fragments/LanguagePackSpecs';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useSystemTranslations from './useSystemTranslations';

export type LanguagePackViewPageProps = { language: LanguagePackSpecsFragment };

const jsonViewerProps: Partial<ReactJsonViewProps> = {
    displayDataTypes: false,
    displayObjectSize: false,
    enableClipboard: false,
    name: null,
    quotesOnKeys: false,
    sortKeys: true,
    shouldCollapse: field => field.name !== null,
};

const LanguagePackViewPage = ({ language }: LanguagePackViewPageProps) => {
    const { t } = useTranslation(['languagePackDetails', 'languagePackList']);
    const navigate = useNavigate();
    const { initialTranslations, loading } = useSystemTranslations();

    const mergedTranslations = useMemo(
        () => mergeTranslations(initialTranslations, language.translations),
        [initialTranslations, language.translations]
    );

    const content = (
        <Descriptions bordered>
            <Descriptions.Item label={t('languagePackList:columns.referenceName')}>
                {language.referenceName}
            </Descriptions.Item>
            <Descriptions.Item label={t('languagePackList:columns.displayName')}>
                {language.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('languagePackList:columns.code')}>{language.code}</Descriptions.Item>
            <Descriptions.Item label={t('languagePackList:columns.orientation')}>
                {t(`languagePackDetails:options.orientation.${language.orientation}`)}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            onBack={() => navigate('/admin/system/languages')}
            title={t('languagePackDetails:title', { name: language.displayName })}
        >
            <Card loading={loading} title={t('languagePackDetails:cardTitles.translations')}>
                {!loading && <ReactJson {...jsonViewerProps} src={mergedTranslations} />}
            </Card>
        </ConsolePageWithHeader>
    );
};

export default LanguagePackViewPage;
